<template>
  <b-modal
    id="create-portfolio-modal"
    size="lg"
    :title="$t('portfolioManager.createPortfolio.createPortfolioFromScratch')"
    cancel-variant="primary"
    ok-variant="secondary"
    :ok-title="$t('general.create')"
    :cancel-title="$t('general.cancel')"
    :ok-disabled="!canSumbit || valueType.length === 0"
    centered
    @ok="handleOkSumbit"
    @show="handleShow"
  >
    <div class="subtitle">
      {{ $t("portfolioManager.createPortfolio.subtitle") }}
    </div>
    <select-box
      label="name"
      v-model="valueType"
      :options="valueTypes"
      :name="$t('portfolioManager.createPortfolio.valueType')"
      :clearable="false"
    />
    <dual-select-box
      :options="tickersOptions"
      :fields="fields"
      @fillPortfolioData="handlePortfolioDataCheck"
      :disabledText="
        valueType.length === 0
          ? $t('portfolioManager.createPortfolio.valueTypeAlert')
          : undefined
      "
    />
    <div class="flex align-baseline">
      <div class="reserve-container">
        <span class="reserve-title">
          {{ $t("portfolioManager.table.cashReserve") }}:
        </span>
        <ValidationProvider name="cashReserve" v-slot="{ errors, failed }">
          <simple-form-input
            v-model="cashReserve"
            :hasError="failed"
            :helperText="errors[0]"
          />
        </ValidationProvider>
        <span class="icon-holder" v-if="valueType.value !== WEIGHT">
          <currency-usd :size="19" />
        </span>
        <span class="icon-holder" v-else>
          <percent-outline :size="19" />
        </span>
      </div>
      <div
        class="sum-container"
        :class="{ red: weightsSum > 100 }"
        v-if="valueType.value === WEIGHT"
      >
        <span>{{ $t("general.sum") }}:</span>
        {{ weightsSum }}%
      </div>
      <div class="sum-container" v-if="valueType.value === CAPITAL">
        <span>{{ $t("general.sum") }}:</span>
        ${{ capitalSum }}
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  AMOUNT,
  CAPITAL,
  RESERVE_TICKER,
  VALUE_OPTIONS,
  WEIGHT,
} from "../../utils/constants";
import { mapActions, mapGetters, mapMutations } from "vuex";
import CurrencyUsd from "vue-material-design-icons/CurrencyUsd.vue";
import DualSelectBox from "../inputs/DualSelectBox.vue";
import PercentOutline from "vue-material-design-icons/PercentOutline.vue";
import SelectBox from "../inputs/SelectBox.vue";
import SimpleFormInput from "../inputs/SimpleFormInput.vue";
export default {
  name: "create-portfolio-modal",
  components: {
    CurrencyUsd,
    DualSelectBox,
    PercentOutline,
    SelectBox,
    SimpleFormInput,
  },
  data() {
    return {
      WEIGHT,
      CAPITAL,
      selectedStocks: [],
      valueTypes: VALUE_OPTIONS(0),
      valueType: [],
      portfolio: [],
      cashReserve: 0,
    };
  },
  computed: {
    ...mapGetters("portfolioManagerStore", {
      allTickers: "getPoolSP100",
      availableTickers: "availableTickers",
      selectedPool: "selectedPool"
    }),
    tickersOptions() {
      return this.availableTickers
        ? Object.entries(this.availableTickers).map(([key, val]) => ({
            ticker: key,
            icon: val,
          }))
        : [];
    },
    fields() {
      const fields = [
        {
          key: "ticker",
          label: this.$i18n.t("portfolioManager.table.stock"),
        },
        {
          key: "value",
          label: this.valueType.name,
        },
      ];
      return fields;
    },
    weightsSum() {
      const sum =
        this.portfolio.reduce(
          (acc, row) => acc + (row.weight ? parseFloat(row.weight) : 0),
          0
        ) + parseFloat(this.cashReserve);
      return Number(sum.toFixed(3));
    },
    capitalSum() {
      const sum = this.portfolio.reduce(
        (acc, row) => acc + parseFloat(row.capital),
        0
      );
      const capitalSum = sum + parseFloat(this.cashReserve);
      return Number(capitalSum.toFixed(3));
    },
    canSumbit() {
      if (this.portfolio.length === 0) {
        return false;
      }
      if (
        this.valueType.value === WEIGHT &&
        Number(this.weightsSum.toFixed(2)) !== 100.0
      ) {
        return false;
      }
      const arr = this.portfolio.map((item) => {
        return Object.values(item).includes(0);
      });
      return !arr.includes(true);
    },
  },
  methods: {
    ...mapActions("portfolioManagerStore", [
      "getSP100Pool",
      "portfolioConversion",
      "calculatePortfolioMetrics",
    ]),
    ...mapMutations("portfolioManagerStore", [
      "setCustomPortfolio",
      "setIsWholeStocks",
    ]),
    handleOkSumbit() {
      if (this.valueType.value === CAPITAL) {
        this.portfolio = this.portfolio.map((item) => {
          return {
            ...item,
            weight: Number(item.capital / this.capitalSum),
          };
        });
      }
      if (this.valueType.value === WEIGHT) {
        this.portfolio = this.portfolio.map((item) => {
          return {
            ...item,
            weight: item.weight / 100,
          };
        });
      }
      if (this.valueType.value === AMOUNT) {
        this.setIsWholeStocks(true);
      }
      if (this.cashReserve) {
        const p_res = parseFloat(this.cashReserve);
        this.portfolio = [
          ...this.portfolio,
          {
            ticker: RESERVE_TICKER,
            amount: 0,
            capital:
              this.valueType.value === AMOUNT ||
              this.valueType.value === CAPITAL
                ? p_res
                : 0,
            weight:
              this.valueType.value === WEIGHT
                ? p_res / 100
                : this.valueType.value === CAPITAL
                ? p_res / this.capitalSum
                : 0,
          },
        ];
      }
      if (this.valueType === CAPITAL || this.valueType === AMOUNT) {
        this.portfolioConversion({ portfolio: this.portfolio }).then(() => {
          this.calculatePortfolioMetrics();
        });
      } else {
        this.setCustomPortfolio({
          ...this.portfolio,
          pool_id: this.selectedPool.pool_id
        });
        this.calculatePortfolioMetrics();
      }
    },
    handlePortfolioDataCheck(data) {
      this.portfolio = data.map((item) => {
        return { ...item, [this.valueType.value]: item.value };
      });
      this.portfolio.forEach((object) => {
        delete object["value"];
      });
    },
    handleShow() {
      this.valueType = [];
    },
  },
  mounted() {
    this.getSP100Pool();
  },
};
</script>

<style lang="scss" scoped>
.subtitle {
  margin-bottom: 1rem;
  font-size: toRem(13);
}
.sum-container {
  font-weight: 600;
  font-size: toRem(13);
  display: flex;
  justify-content: flex-end;
  margin-left: auto;
  &.red {
    color: $errorRedBorder;
  }
  span {
    margin-right: toRem(5);
  }
}
.reserve-container {
  display: flex;
  align-items: baseline;
  margin-left: 40%;
  margin-right: toRem(15);
  .reserve-title {
    color: $primaryColor;
    font-weight: 500;
    margin-right: toRem(5);
    font-size: toRem(13);
    white-space: nowrap;
  }
}
</style>